import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';
import { toast } from 'react-toastify';

const projectUrl = '/crm/projects';

// Get price matrices with server-side pagination, sorting, and filtering
export const getPriceMatrices = createAsyncThunk(
  'priceMatrix/getPriceMatrices',
  async ({ params }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/price_matrices?${params}`);
      return response.data;
    } catch (error) {
      console.error('Get price matrices error:', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch price matrices'
      );
    }
  }
);

// Export price matrix
export const exportPriceMatrix = createAsyncThunk(
  'priceMatrix/exportPriceMatrix',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/price_matrices_export?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `price_matrices_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Price matrix exported successfully');
      return true;
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export price matrix');
      return rejectWithValue(error.message);
    }
  }
);

export const createPriceMatrix = createAsyncThunk(
  'crm_project_price_matrix/createPriceMatrix',
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/price_matrices`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updatePriceMatrix = createAsyncThunk(
  'crm_project_price_matrix/updatePriceMatrix',
  async ({ projectId, priceMatrixId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${projectUrl}/${projectId}/price_matrices/${priceMatrixId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


// Delete price matrix
export const deletePriceMatrix = createAsyncThunk(
  'crm_project_price_matrix/deletePriceMatrix',
  async ({ projectId, priceMatrixId }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.delete(`${projectUrl}/${projectId}/price_matrices/${priceMatrixId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  data: [],
  loading: false,
  error: null,
  paginate: {
    total_rows: 0,
    current_page: 1,
    prev_page: null,
    next_page: null,
    per_page: 30,
    total_pages: 1
  },
  currentParams: {},
  exporting: false
};

const priceMatrixSlice = createSlice({
  name: 'crm_project_price_matrix',
  initialState,
  reducers: {
    setCurrentParams: (state, action) => {
      state.currentParams = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetPriceMatrix: (state) => {
      state.data = [];
      state.paginate = initialState.paginate;
      state.currentParams = {};
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get price matrices
      .addCase(getPriceMatrices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPriceMatrices.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        // Extract data from the response structure
        const response = action.payload;
        state.data = response.rows ? response.rows.map(item => ({
          id: item.id,
          ...item.attributes
        })) : [];
        
        // Update pagination info
        state.paginate = {
          total_rows: response.total_rows || 0,
          current_page: response.current_page || 1,
          prev_page: response.prev_page,
          next_page: response.next_page,
          per_page: response.per_page || 30,
          total_pages: response.total_pages || 1
        };
      })
      .addCase(getPriceMatrices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = [];
      })
      
      // Export price matrix
      .addCase(exportPriceMatrix.pending, (state) => {
        state.exporting = true;
      })
      .addCase(exportPriceMatrix.fulfilled, (state) => {
        state.exporting = false;
      })
      .addCase(exportPriceMatrix.rejected, (state) => {
        state.exporting = false;
      })
      
      // Create price matrix
      .addCase(createPriceMatrix.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPriceMatrix.fulfilled, (state, action) => {
        state.loading = false;

        const payload = action.payload || {};
        const { data, message } = payload;

        // Handle data insertion if data exists
        if (data && Array.isArray(state.data)) {
          // Transform the data to match the list format
          const newItem = data.attributes
            ? { id: data.id, ...data.attributes }
            : { id: data.id, ...data };

          // Add to beginning of array
          state.data = [newItem, ...state.data];

          // Update pagination
          state.paginate = {
            ...state.paginate,
            total_rows: state.paginate.total_rows + 1
          };
        }

        // Show success message if available
        if (message) {
          toast.success(message);
        }
      })
      .addCase(createPriceMatrix.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create price matrix';
      })

      // Update price matrix
      .addCase(updatePriceMatrix.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePriceMatrix.fulfilled, (state, action) => {
        state.loading = false;

        const payload = action.payload || {};
        const { data, message } = payload;

        if (data && Array.isArray(state.data)) {
          const index = state.data.findIndex(item => item.id === data.id);
          if (index !== -1) {
            // Update with proper structure
            const updatedItem = data.attributes
              ? { id: data.id, ...data.attributes }
              : { id: data.id, ...data };
            state.data[index] = updatedItem;
          }
        }

        if (message) {
          toast.success(message);
        }
      })
      .addCase(updatePriceMatrix.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update price matrix';
      })

      // Delete price matrix
      .addCase(deletePriceMatrix.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePriceMatrix.fulfilled, (state, action) => {
        state.loading = false;

        const payload = action.payload || {};
        const { data, message } = payload;

        if (data && Array.isArray(state.data)) {
          state.data = state.data.filter(item => item.id !== data.id);

          // Update pagination
          state.paginate = {
            ...state.paginate,
            total_rows: Math.max(0, state.paginate.total_rows - 1)
          };
        }

        if (message) {
          toast.success(message);
        }
      })
      .addCase(deletePriceMatrix.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete price matrix';
      })
  }
});

export const { setCurrentParams, clearError, resetPriceMatrix } = priceMatrixSlice.actions;
export default priceMatrixSlice.reducer;